/* eslint-disable react/jsx-no-bind */
/* istanbul ignore file */
import React, { useEffect, useRef, useState } from 'react';
import { TextField, ITextFieldStyles } from '@fluentui/react';
import { isValidUrl } from '@bingads-webui/url-util';
import { ActivityWithLogger } from '@bingads-webui/instrumentation';
import { ImagePickerContainer } from '@bingads-webui-react/image-picker';
import SocialMediaRepository from '../../util/social-media-repository';
import SocialMediaDataSource, { IImageItem, FetchImageResult } from '../data-source/social-media-data-source';
import LoadingPlaceholder from '../../components/loading-place-holder';
import FinalUrlInput from '../../components/final-url-input';
import C from '../../constants';

declare let _TL_: any;
interface IProps {
  repository: SocialMediaRepository;
  i18n: any;
  finalUrlProtocol: string;
  finalUrl: string;
  activityLogger: ActivityWithLogger;
  onImageSelectedByUrl: (url: string, sourcetype: string) => void;
  onUpdateFinalUrlPrefix:
    (event: React.FormEvent<HTMLDivElement>, option?: any, index?: number) => void;
  onUpdateFinalUrl: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

enum FetchStatus {
  Fetching = 'Fetching',
  Done = 'Done',
}

const useConstructor = (callback: Function) => {
  const hasBeenCalled = useRef(false);

  if (hasBeenCalled.current) {
    return;
  }
  callback();
  hasBeenCalled.current = true;
};

const twitterReg = new RegExp(/(http:\/\/|https:\/\/)?(www\.)?twitter.com\/(\w+)\/?/);

export default function SocialMediaPickerContainer(props: IProps) {
  const {
    i18n,
    repository,
    finalUrl,
    onUpdateFinalUrl,
    onImageSelectedByUrl,
    activityLogger,
    onUpdateFinalUrlPrefix,
    finalUrlProtocol,
  } = props;

  const [dataSource, setDataSource] = useState<SocialMediaDataSource | undefined>(undefined);
  const [fetchStatus, setFetchStatus] = useState<FetchStatus>(FetchStatus.Fetching);
  const [alertMessage, setAlertMessage] = useState<string>();
  const [account, setAccount] = useState<string>();

  useConstructor(() => {
    setDataSource(new SocialMediaDataSource({ repository }));
  });

  useEffect(() => {
    if (isValidUrl(finalUrl) && dataSource) {
      setFetchStatus(FetchStatus.Fetching);

      dataSource.fetchImages(finalUrl).then((result) => {
        setAlertMessageByResult(result);

        activityLogger.signal(`${finalUrl}, ${dataSource.data.length}, ${result}`, 'social media image fetched');

        if (result !== FetchImageResult.NoAccountFound) {
          setAccount(`https://twitter.com/${dataSource.socialMediaAccounts[0].Value}`);
        }

        setFetchStatus(FetchStatus.Done);
      });
    }
  }, [dataSource, finalUrl]);

  function setAlertMessageByResult(result: FetchImageResult) {
    if (result === FetchImageResult.NoAccountFound) {
      /* istanbul ignore next */
      setAlertMessage(i18n.getString(_TL_('No Twitter account found from your URL. Please enter your Twitter account URL manually')));
    } else if (result === FetchImageResult.NoImagesFound) {
      /* istanbul ignore next */
      setAlertMessage(i18n.getString(_TL_('No images posted recently on your Twitter account.')));
    } else if (result === FetchImageResult.Done) {
      setAlertMessage(i18n.getString(_TL_("We've found images from your Twitter account.")));
    }
  }

  function onImageSelected(item: IImageItem[]) {
    activityLogger.signal('', 'social media image selected');
    onImageSelectedByUrl(item[0].src, C.sourceTypes.TwitterImported);
  }

  /* istanbul ignore next */
  function onAccountChange(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) {
    setAccount(newValue);
  }

  function onBlur() {
    const result = account.match(twitterReg);

    /* istanbul ignore next */
    if (result && result[3]) {
      setAlertMessage('');
      dataSource.reset();
      // eslint-disable-next-line @typescript-eslint/no-shadow
      dataSource.fetchImageByAccount([{ Key: 'Twitter', Value: result[3] }]).then((result) => {
        setAlertMessageByResult(result);
        activityLogger.signal('', 'social media image: manually input');
      });
    } else {
      setAlertMessage(i18n.getString(_TL_('Please enter the correct Twitter URL, like: https://twitter.com/microsoft')))
    }
  }

  if (!isValidUrl(finalUrl)) {
    return (
      <div className="social-media-container">
        <FinalUrlInput
          invalidURL={finalUrl && !isValidUrl(finalUrl)}
          i18n={i18n}
          finalUrl={finalUrl}
          alertString={i18n.getString('ImageExperience_InvalidURL')}
          onUpdateFinalUrl={onUpdateFinalUrl}
          onUpdateFinalUrlPrefix={onUpdateFinalUrlPrefix}
          finalUrlProtocol={finalUrlProtocol}
        />
      </div>
    );
  }

  if (!dataSource) {
    return null;
  }

  return (
    <div className="social-media-container">
      {fetchStatus === FetchStatus.Done && <TextField
        label={alertMessage}
        value={account}
        iconProps={{ iconName: 'Search' }}
        className="desc"
        onBlur={onBlur}
        onChange={onAccountChange}
        styles={{ fieldGroup: { width: 300 } } as Partial<ITextFieldStyles>}
      />}
      <ImagePickerContainer
        i18n={i18n}
        className="social-media"
        dataSource={dataSource}
        onSelectionChanged={onImageSelected}
        isMultiSelection={false}
        isLazyLoadEnabled
        maximizeFrame
        itemHeight={180}
        useBingAdsLoader
        imagePlaceHolder={LoadingPlaceholder}
      />
    </div>
  );
}

